import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { ILeadView } from '../../viewModels/leads/interfaces';
import { queryKeys } from './queryKeys';
import { FocusedLead, fromILeadView } from './types';

interface NextLeadVariables {
	ruleId?: string;
	/**
	 * When `true`, request a practice lead via `mode=PracticeCall`. This returns a
	 * REAL lead — the backend just doesn't remove it from the available queue, so
	 * rehearsing on it doesn't consume it. Mirrors the legacy `usePracticeLead`
	 * request shape (`GET lead?mode=PracticeCall&previousCompanyIds=...`).
	 */
	practice?: boolean;
	/**
	 * Company ids already served during this practice session, sent so the
	 * backend can avoid repeating them. Joined comma-separated, and only
	 * included when non-empty. Ignored on the non-practice path.
	 */
	previousCompanyIds?: string[];
}

/**
 * Pops the next lead out of the queue. Replicates the request shape of
 * `LeadsViewModel.getNext` — a plain `GET /lead?ruleId=...` — without pulling
 * in the MobX viewmodel itself.
 *
 * When `variables.practice` is `true`, the request instead matches the legacy
 * `usePracticeLead` shape: `mode=PracticeCall` plus a comma-joined
 * `previousCompanyIds` (omitted when empty). The standard (non-practice) path
 * is unchanged.
 *
 * On success, the new lead is primed into the `focused-view/lead/:companyId`
 * cache so that components reading from `useLead` render immediately without
 * a flash of loading state (see data/README.md).
 *
 * A 404 from the API means "no more leads" — we model that as a successful
 * mutation resolving to `null`.
 */
export function useNextLead(): UseMutationResult<FocusedLead | null, Error, NextLeadVariables | void> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<FocusedLead | null, Error, NextLeadVariables | void>({
		mutationFn: async variables => {
			const ruleId = variables ? variables.ruleId : undefined;
			const queryParams: Record<string, string | undefined> = { ruleId };

			if (variables && variables.practice) {
				queryParams.mode = 'PracticeCall';
				const previousCompanyIds = variables.previousCompanyIds ?? [];
				if (previousCompanyIds.length > 0) {
					queryParams.previousCompanyIds = previousCompanyIds.join(',');
				}
			}

			const result = await userSession.webServiceHelper.callWebServiceAsync<ILeadView>(
				ImpersonationBroker.composeApiUrl({ urlPath: 'lead', queryParams }),
				'GET'
			);

			if (result.success) {
				return result.value ? fromILeadView(result.value) : null;
			}

			if (result.systemCode === 404) {
				return null;
			}

			throw asApiError(result);
		},
		onSuccess: focusedLead => {
			if (focusedLead?.companyId) {
				queryClient.setQueryData(queryKeys.focusedView.lead(focusedLead.companyId), focusedLead);
			}
		},
	});
}
